// The agent names products by linking to them. Everything that wants to show
// those products as something better than a line of text starts here.

// Matches the URLs the agent writes: https://a-shop.com/products/mango-cake/123
const PRODUCT_URL = /products\/[^/]+\/(\d+)/g;

export const extractProductIds = (text) => {
  if (!text || typeof text !== "string") return [];
  const ids = [];
  let match;
  // eslint-disable-next-line no-cond-assign
  while ((match = PRODUCT_URL.exec(text)) !== null) {
    const id = parseInt(match[1], 10);
    if (!Number.isNaN(id)) ids.push(id);
  }
  PRODUCT_URL.lastIndex = 0;
  return Array.from(new Set(ids));
};

// The products a finished answer carries with it. Since nasnav-backend !2503
// (MEE-501) the server attaches what its catalogue tools returned to the end of
// a streamed answer as `products: [{ id, name, price, image, url }]`. That is
// the agent's own record of what it found, so it is preferred over reading
// product links out of the prose, which the agent does not always write.
// An empty or missing list means "nothing carried"; callers fall back to links.
export const carriedProducts = (message) => {
  const list = message && message.products;
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  return list.filter((product) => {
    if (!product || !product.name) return false;
    const key = product.id != null ? product.id : product.name;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// A product page on the brand's own site, not ours. The whole point of being a
// script on their shop rather than a replacement for it is that the customer
// lands somewhere with the brand's header, footer and checkout.
export const productPageUrl = (product, shopUrl) => {
  const base = String(shopUrl || "").replace(/\/$/, "");
  return `${base}/products/${product.p_name}/${product.id}`;
};

// What the agent writes for a link: a picture, a labelled link, or the bare
// address. The products it links to are shown as cards of their own, so the
// address itself is never worth printing.
const MARKDOWN_IMAGE = /!\[[^\]]*\]\([^)]*\)/g;
const MARKDOWN_LINK = /\[([^\]]*)\]\([^)]*\)/g;
// Ends before any sentence punctuation that follows it, so the full stop
// after "...here: https://shop/p." stays with the sentence.
const BARE_ADDRESS = /\bhttps?:\/\/\S*[^\s.,;:!?)]/g;
// Where an answer that lists products starts its list: " 1. ".
const NUMBERED_LIST = /\s+1\.\s+/;
const QUESTION_END = /[?؟？]$/;

const withoutLinks = (text) =>
  text
    .replace(MARKDOWN_IMAGE, "")
    .replace(MARKDOWN_LINK, "$1")
    .replace(BARE_ADDRESS, "")
    .replace(/\(\s*\)/g, "")
    .replace(/[ \t]+([,.;:!?])/g, "$1")
    .replace(/:([.!?])/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .trim();

/**
 * The words of an answer that names products, to print above their cards.
 *
 * The panel used to print only the sentence before a numbered list, and
 * nothing at all when there was no list. On the live Tseppas shop 2026-10-02
 * a one-product answer ("The Pack of Koras with Date is a great alternative
 * ... Want to try one of these? https://...") showed as a bare product card
 * under the agent's name: every word of it was dropped.
 *
 * Without a list it is the whole answer, minus the addresses. With a list the
 * items are the cards, so it is the sentence before the list, plus the closing
 * question when the answer ends on one.
 *
 * @param {string} text the agent's answer
 * @returns {string} what to print above the product cards; '' for nothing
 */
export const answerWords = (text) => {
  if (!text || typeof text !== "string") return "";
  const words = withoutLinks(text);
  const list = words.match(NUMBERED_LIST);
  if (!list || list.index === 0) return words;

  const intro = words.slice(0, list.index).trim();
  if (!QUESTION_END.test(words)) return intro;
  const lastBreak = Math.max(
    words.lastIndexOf(". "),
    words.lastIndexOf("! "),
    words.lastIndexOf("\n")
  );
  const closing = lastBreak > list.index ? words.slice(lastBreak + 1).trim() : "";
  return closing ? `${intro} ${closing}` : intro;
};
